import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { packagesAPI } from '../../services/api';
import Button from '../common/Button';
import Card from '../common/Card';
import PackageFormModal from '../packages/PackageFormModal';
import { handleApiError, showToast, showConfirm } from '../../utils/helpers';

const ClientPackagesSection = ({ clientId }) => {
  const { isRTL } = useLanguage();
  const [clientPackages, setClientPackages] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [allAgencyPackages, setAllAgencyPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState(null);
  const [showCreateCustomModal, setShowCreateCustomModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [customPostLimit, setCustomPostLimit] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientPkgRes, allPkgRes] = await Promise.all([
        packagesAPI.getClientPackages(clientId),
        packagesAPI.getAll({ params: { forClient: clientId } })
      ]);
      
      setClientPackages(clientPkgRes.data.packages || []);
      
      // Filter out packages already assigned (active) to this client
      const assignedIds = (clientPkgRes.data.packages || [])
        .filter(p => p.status === 'active')
        .map(p => p.package_id);
      const allPkgs = allPkgRes.data.packages || [];
      const available = allPkgs
        .filter(p => p.is_active && !assignedIds.includes(p.id));
      setAllAgencyPackages(allPkgs.filter(p => p.is_active));
      setAvailablePackages(available);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId]);

  const handleAssignPackage = async () => {
    if (!selectedPackage) return;
    
    try {
      setAdding(true);
      const payload = { package_id: selectedPackage };
      if (customPostLimit && parseInt(customPostLimit, 10) > 0) {
        payload.custom_post_limit = parseInt(customPostLimit, 10);
      }
      await packagesAPI.assignToClient(clientId, payload);
      setShowAddModal(false);
      setSelectedPackage('');
      setCustomPostLimit('');
      fetchData();
      showToast.success(isRTL ? 'تم إضافة الباقة بنجاح!' : 'Package assigned successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const handleRemovePackage = async (clientPackageId) => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من إزالة هذه الباقة؟' : 'Are you sure you want to remove this package?',
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      await packagesAPI.removeFromClient(clientPackageId);
      fetchData();
      showToast.success(isRTL ? 'تم إزالة الباقة بنجاح!' : 'Package removed successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600',
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: { en: 'Active', ar: 'نشطة' },
      completed: { en: 'Completed', ar: 'مكتملة' },
      expired: { en: 'Expired', ar: 'منتهية' },
      cancelled: { en: 'Cancelled', ar: 'ملغاة' },
    };
    const label = labels[status] || { en: status, ar: status };
    return isRTL ? label.ar : label.en;
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          📦 {isRTL ? 'باقات العميل' : 'Client Packages'}
        </h3>
        <Button size="sm" onClick={() => { setShowAddModal(true); setAddMode(null); }}>
          {isRTL ? '+ إضافة باقة' : '+ Add Package'}
        </Button>
      </div>

      {clientPackages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">📦</div>
          <p>{isRTL ? 'لا توجد باقات مسجلة لهذا العميل' : 'No packages assigned to this client'}</p>
          {allAgencyPackages.length === 0 ? (
            <div className="mt-4">
              <p className="text-sm mb-2">
                {isRTL ? 'لا توجد باقات. أنشئ باقات أولاً من قسم الباقات.' : 'No packages yet. Create packages first in the Packages section.'}
              </p>
              <Link to="/packages" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                {isRTL ? '→ الذهاب إلى الباقات' : '→ Go to Packages'}
              </Link>
            </div>
          ) : availablePackages.length === 0 ? (
            <div className="mt-4">
              <p className="text-sm mb-2">
                {isRTL ? 'جميع الباقات مسجلة لهذا العميل' : 'All packages are already assigned to this client'}
              </p>
              <Button size="sm" className="mt-2" onClick={() => { setShowAddModal(true); setAddMode(null); }}>
                {isRTL ? 'إنشاء باقة مخصصة' : 'Create Custom Package'}
              </Button>
            </div>
          ) : (
            <Button size="sm" className="mt-4" onClick={() => { setShowAddModal(true); setAddMode(null); }}>
              {isRTL ? 'إضافة باقة' : 'Add Package'}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {clientPackages.map((pkg) => (
            <div 
              key={pkg.id} 
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-gray-900">
                      {isRTL && pkg.package_name_ar ? pkg.package_name_ar : pkg.package_name}
                    </h4>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(pkg.status)}`}>
                      {getStatusLabel(pkg.status)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">{isRTL ? 'المستخدم' : 'Used'}</span>
                      <p className="font-semibold">{pkg.posts_used} / {pkg.original_limit}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">{isRTL ? 'المتبقي' : 'Remaining'}</span>
                      <p className={`font-semibold ${pkg.posts_remaining <= 3 ? 'text-red-600' : 'text-green-600'}`}>
                        {pkg.posts_remaining}
                      </p>
                    </div>
                    {pkg.platform && (
                      <div>
                        <span className="text-gray-500">{isRTL ? 'المنصة' : 'Platform'}</span>
                        <p className="font-medium capitalize">{pkg.platform}</p>
                      </div>
                    )}
                    {pkg.end_date && (
                      <div>
                        <span className="text-gray-500">{isRTL ? 'تنتهي في' : 'Expires'}</span>
                        <p className="font-medium">{new Date(pkg.end_date).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          pkg.posts_remaining <= 0 ? 'bg-red-500' :
                          pkg.posts_remaining <= 3 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${(pkg.posts_used / pkg.original_limit) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {pkg.status === 'active' && (
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={() => handleRemovePackage(pkg.id)}
                    className={isRTL ? 'mr-4' : 'ml-4'}
                  >
                    {isRTL ? 'إزالة' : 'Remove'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Package Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {isRTL ? 'إضافة باقة للعميل' : 'Add Package to Client'}
            </h3>

            {addMode === null ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  {isRTL ? 'كيف تريد إضافة الباقة؟' : 'How would you like to add the package?'}
                </p>
                {availablePackages.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setAddMode('template')}
                    className="w-full p-4 text-right border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 block">
                      {isRTL ? 'اختيار من قوالب الباقات' : 'Select from package templates'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {isRTL ? 'اختر من الباقات الموجودة' : 'Choose from existing packages'}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddMode(null);
                    setShowCreateCustomModal(true);
                  }}
                  className="w-full p-4 text-right border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 block">
                    {isRTL ? 'إنشاء باقة مخصصة' : 'Create custom package'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {isRTL ? 'أنشئ باقة بخصائص مخصصة لهذا العميل فقط' : 'Create a package with custom specs for this client only'}
                  </span>
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'اختر الباقة' : 'Select Package'}
                  </label>
                  <select
                    value={selectedPackage}
                    onChange={(e) => setSelectedPackage(e.target.value)}
                    className="input"
                  >
                    <option value="">{isRTL ? '-- اختر باقة --' : '-- Select Package --'}</option>
                    {availablePackages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {isRTL && pkg.name_ar ? pkg.name_ar : pkg.name} ({pkg.post_limit} {isRTL ? 'بوست' : 'posts'})
                        {pkg.price > 0 && ` - ${pkg.price} ${pkg.currency}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isRTL ? 'عدد المنشورات المخصص (اختياري)' : 'Custom post limit (optional)'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder={selectedPackage ? (availablePackages.find(p => p.id === selectedPackage)?.post_limit || '') : ''}
                    value={customPostLimit}
                    onChange={(e) => setCustomPostLimit(e.target.value)}
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isRTL ? 'اتركه فارغاً لاستخدام القيمة الافتراضية للباقة' : 'Leave empty to use package default'}
                  </p>
                </div>
              </>
            )}

            <div className={`flex items-center justify-end mt-6 ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
              {addMode === 'template' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setAddMode(null)}
                  className={isRTL ? 'ml-2' : 'mr-2'}
                >
                  {isRTL ? '← رجوع' : '← Back'}
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setAddMode(null);
                  setSelectedPackage('');
                  setCustomPostLimit('');
                }}
                disabled={adding}
              >
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              {addMode === 'template' && (
                <Button
                  variant="primary"
                  onClick={handleAssignPackage}
                  loading={adding}
                  disabled={!selectedPackage || adding}
                >
                  {isRTL ? 'إضافة' : 'Add'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Package Modal */}
      {showCreateCustomModal && (
        <PackageFormModal
          clientId={clientId}
          onClose={() => setShowCreateCustomModal(false)}
          onSaved={() => {
            setShowCreateCustomModal(false);
            fetchData();
            showToast.success(isRTL ? 'تم إنشاء الباقة وتكليفها للعميل!' : 'Package created and assigned successfully!');
          }}
        />
      )}
    </Card>
  );
};

export default ClientPackagesSection;






